import React, { useEffect } from 'react';
import { Box, Typography, Button, IconButton, Paper, Avatar } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  ShoppingCart as ShoppingCartIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import { getProductImage } from '../../utils/imageHelper';

const CartNotificationToast = () => {
  const navigate = useNavigate();
  const { cartNotification, closeCartNotification, subtotal, totalItems } = useCart();

  useEffect(() => {
    if (cartNotification.open) {
      const timer = setTimeout(() => {
        closeCartNotification();
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [cartNotification.open, cartNotification.timestamp, closeCartNotification]);

  const item = cartNotification.item;

  return (
    <AnimatePresence>
      {cartNotification.open && item && (
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: -40, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -30, scale: 0.95 }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
          sx={{
            position: 'fixed',
            top: { xs: 80, sm: 24 },
            right: { xs: 16, sm: 24 },
            zIndex: 9999,
            maxWidth: { xs: 'calc(100vw - 32px)', sm: 390 },
            width: '100%',
          }}
        >
          <Paper
            elevation={12}
            sx={{
              p: 2.2,
              borderRadius: 3,
              backgroundColor: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(16px)',
              border: '1px solid rgba(14, 106, 218, 0.18)',
              boxShadow: '0 16px 40px -8px rgba(0, 0, 0, 0.22), 0 0 0 1px rgba(0,0,0,0.05)',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            {/* Top Success Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ color: '#10B981', fontSize: 22 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#111827', fontSize: '0.92rem' }}>
                  Added to Cart!
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={closeCartNotification}
                sx={{
                  color: 'text.secondary',
                  p: 0.5,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            {/* Product Info Row */}
            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                alignItems: 'center',
                p: 1.2,
                borderRadius: 2,
                bgcolor: '#F8FAFC',
                border: '1px solid #E2E8F0',
                mb: 1.8,
              }}
            >
              <Avatar
                variant="rounded"
                src={getProductImage(item.featuredImage || item.image)}
                alt={item.name}
                sx={{
                  width: 52,
                  height: 52,
                  bgcolor: '#FFF',
                  p: 0.5,
                  border: '1px solid #E2E8F0',
                  '& img': { objectFit: 'contain' },
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: '#0F172A',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: '0.88rem',
                  }}
                >
                  {item.name}
                </Typography>
                <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>
                  {cartNotification.size && `License: ${cartNotification.size} • `}Qty: {cartNotification.quantity}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 800, color: '#0E6ADA', fontSize: '0.88rem' }}>
                  ₹{item.price || item.currentPrice || 0}
                </Typography>
              </Box>
            </Box>

            {/* Cart Summary Line */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, px: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
                Cart Subtotal ({totalItems} item{totalItems > 1 ? 's' : ''}):
              </Typography>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#111827' }}>
                ₹{subtotal.toLocaleString('en-IN')}
              </Typography>
            </Box>

            {/* Action Buttons */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 1.2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ShoppingCartIcon fontSize="small" />}
                onClick={() => {
                  closeCartNotification();
                  navigate('/cart');
                }}
                sx={{
                  py: 0.9,
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  borderRadius: 2,
                  borderColor: '#CBD5E1',
                  color: '#1E293B',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#0E6ADA',
                    backgroundColor: 'rgba(14, 106, 218, 0.04)',
                  },
                }}
              >
                View Cart
              </Button>
              <Button
                variant="contained"
                size="small"
                endIcon={<ArrowForwardIcon fontSize="small" />}
                onClick={() => {
                  closeCartNotification();
                  navigate('/checkout');
                }}
                sx={{
                  py: 0.9,
                  fontWeight: 700,
                  fontSize: '0.82rem',
                  borderRadius: 2,
                  bgcolor: '#0E6ADA',
                  boxShadow: '0 4px 14px rgba(14, 106, 218, 0.3)',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: '#0c57b3',
                    boxShadow: '0 6px 18px rgba(14, 106, 218, 0.4)',
                  },
                }}
              >
                Checkout
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
    </AnimatePresence>
  );
};

export default CartNotificationToast;
